'use client';

import * as DS from '@aurorasolar/ds';
import Image, { type ImageProps, type StaticImageData } from 'next/image';
import { Children, ReactNode, useMemo } from 'react';

type Spacing = number | string | { _?: number | string; lg?: number | string; md?: number | string };

type Props = {
  align?: 'center';
  bg?: string;
  bgImage?: StaticImageData;
  children?: ReactNode;
  colorMode?: 'light' | 'dark';
  columnGap?: number | string;
  columns?: 1 | 2 | 3 | 4;
  imageLeft?: ImageProps;
  imageRight?: ImageProps;
  maxW?: number | string;
  pb?: Spacing;
  pt?: Spacing;
  px?: Spacing;
  rowGap?: number | string;
};

const getGridSize = (columns?: number) => {
  if (columns === 2) return { _: 'full', md: 'half' } as const;
  if (columns === 3) return { _: 'full', lg: 'third', md: 'half' } as const;
  if (columns === 4) return { _: 'full', lg: 'quarter', md: 'half' } as const;
  return 'full' as const;
};

const SectionImage = ({ image }: { image: ImageProps }) => {
  const minH = typeof image.height === 'string' ? parseInt(image.height, 10) : image.height;

  return (
    <DS.Flex
      minH={{ _: minH ? minH * 0.5 : undefined, lg: minH, md: minH ? minH * 0.75 : undefined }}
      position="relative"
    >
      <Image
        alt={image.alt}
        fill
        sizes="(max-width: 768px) 100vw, 50vw"
        src={image.src}
        style={{ objectFit: 'contain' }}
      />
    </DS.Flex>
  );
};

export const Section = ({
  align,
  bg,
  bgImage,
  children,
  colorMode = 'light',
  columnGap = 4,
  columns = 1,
  imageLeft,
  imageRight,
  maxW = DS.BorealisTheme.screens.xl,
  pb = { _: 7, lg: 10 },
  pt = { _: 7, lg: 10 },
  px = { _: 6, lg: 10 },
  rowGap,
}: Props) => {
  const textAlign = align === 'center' ? 'center' : undefined;
  const ModeProvider = colorMode === 'dark' || bgImage ? DS.DarkModeProvider : DS.LightModeProvider;

  const cols = imageLeft || imageRight ? 2 : columns;

  const childNodes: ReactNode[] = useMemo(() => {
    if (imageLeft) return [<SectionImage image={imageLeft} />, children];
    if (imageRight) return [children, <SectionImage image={imageRight} />];

    return cols === 1 ? [children] : Children.toArray(children);
  }, [children, cols, imageLeft, imageRight]);

  return (
    <ModeProvider>
      <DS.Flex bg={bg ?? (colorMode === 'dark' ? 'uiBgAlternate' : undefined)}>
        <DS.Flex maxW={maxW} mx="auto" pb={pb} position="relative" pt={pt} px={px}>
          {bgImage && <Image alt="" fill src={bgImage} style={{ filter: 'brightness(40%)', objectFit: 'cover' }} />}

          {/* Gutter must not apply below md — items are single-column there and DS.Grid still lays
              out all 11 column gaps, forcing a minimum width of 11*columnGap. See ColumnSection. */}
          <DS.Grid columnGap={{ _: 0, md: columnGap }} flex={1} rowGap={rowGap} zIndex={1}>
            {childNodes.map((child, i) => (
              <DS.GridItem
                alignItems={textAlign}
                gap={4}
                justifyContent="center"
                key={i} // eslint-disable-line react/no-array-index-key
                order={{ _: imageRight && i === 0 ? 2 : 1, md: i + 1 }} // if imageRight, swap order of text and image on mobile assuming just 2 columns, keep original order on desktop
                size={getGridSize(cols)}
                textAlign={textAlign}
              >
                {child}
              </DS.GridItem>
            ))}
          </DS.Grid>
        </DS.Flex>
      </DS.Flex>
    </ModeProvider>
  );
};
